import "./Preloader.css";
import PropTypes from "prop-types";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useContextAwareness } from "../../hooks/useContextAwareness";

gsap.registerPlugin(useGSAP);

// Content arrives from "near" (scaled up, blurred), leaves to "far"
const NEAR = { scale: 1.12, filter: "blur(12px)", autoAlpha: 0 };
const FAR = { scale: 0.88, filter: "blur(16px)", autoAlpha: 0 };

const WEIGHT_MIN = 200; // Clash Display variable range
const WEIGHT_MAX = 700;
const WAVE = 2; // letters a weight change spreads across
const SECONDS_PER_100 = 1.8; // pace of the wave across the whole title

const localTime = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });

// Title letters thicken left-to-right as assets load (the progress bar),
// with the changing letters flickering like projected film.
function Preloader({ onLoadComplete, text1, text2 }) {
  const container = useRef();
  const progress = useRef({ value: 0 });
  const [loadProgress, setLoadProgress] = useState(0);
  const [introDone, setIntroDone] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [done, setDone] = useState(false);
  const { timeOfDay, source } = useContextAwareness();
  const title = text1 || "MEDAYOUBCHEBBI.";
  // Under the role line: the page's own subtitle, or time + where they came from
  const desc = text2 ?? [localTime(), timeOfDay, source && `from ${source}`].filter(Boolean).join(" · ");

  // Track loading progress
  useEffect(() => {
    let totalAssets = 0;
    let loadedAssets = 0;

    const updateProgress = () => {
      loadedAssets++;
      const progress = totalAssets > 0 ? Math.round((loadedAssets / totalAssets) * 100) : 100;
      setLoadProgress((p) => Math.max(p, progress));
    };

    // Lazy images load on scroll, so waiting on them would stall the preloader.
    const images = document.querySelectorAll("img:not([loading=\"lazy\"])");
    totalAssets += images.length;
    images.forEach((img) => {
      if (img.complete) updateProgress();
      else {
        img.addEventListener("load", updateProgress);
        img.addEventListener("error", updateProgress);
      }
    });

    // Videos load on their own once the page is up; waiting on them made the
    // site take a minute to open.

    totalAssets += 1;
    document.fonts.ready.then(updateProgress);

    // Slow network: open the site after 3s anyway
    const cap = setTimeout(() => setLoadProgress(100), 3000);
    return () => clearTimeout(cap);
  }, []);

  // IN: background is opaque from the first frame (it covers the new page);
  // the content arrives from "near", then letters rise in one by one (same
  // as the navbar wordmark)
  useGSAP(
    () => {
      gsap.set(".preloader-char", { fontWeight: WEIGHT_MIN });
      gsap
        .timeline({ onComplete: () => setIntroDone(true) })
        .fromTo(".preloader-content", NEAR, { scale: 1, filter: "blur(0px)", autoAlpha: 1, duration: 0.8, ease: "power3.out" })
        .fromTo(".preloader-char", { yPercent: 110 }, { yPercent: 0, duration: 0.6, stagger: 0.025, ease: "power3.out" }, "-=0.4")
        .fromTo(".preloader-desc", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.6 }, "-=0.3");
    },
    { scope: container },
  );

  // Weight wave follows load progress, running alongside the intro
  useGSAP(
    () => {
      const chars = gsap.utils.toArray(".preloader-char");
      const n = chars.length;
      const target = loadProgress;

      const render = () => {
        const front = (progress.current.value / 100) * (n + WAVE);
        chars.forEach((el, i) => {
          const t = gsap.utils.clamp(0, 1, (front - i) / WAVE);
          el.style.fontWeight = Math.round(WEIGHT_MIN + (WEIGHT_MAX - WEIGHT_MIN) * t);
          // ponytail: Math.random flicker per frame, swap for a seeded noise if it reads too jittery
          el.style.opacity = t > 0 && t < 1 ? 0.55 + Math.random() * 0.45 : 1;
        });
      };

      gsap.to(progress.current, {
        value: target,
        duration: Math.max(0.3, ((target - progress.current.value) / 100) * SECONDS_PER_100),
        ease: "none",
        overwrite: true,
        onUpdate: render,
        onComplete: () => target >= 100 && setLoaded(true),
      });
    },
    { scope: container, dependencies: [loadProgress] },
  );

  // OUT once both the intro and the wave are done: recedes to "far";
  // onLoadComplete fires as it starts so the page intro plays while the
  // preloader is still leaving. Then re-measure every ScrollTrigger: pins
  // were sized before fonts/images loaded.
  // Done = unmounted, not display: none. While the next route's chunk loads,
  // Suspense keeps this page mounted but hidden and reverts its GSAP, which
  // would bring a merely-hidden preloader (portaled, so not hidden with the
  // page) back with the old title.
  useGSAP(
    () => {
      if (!introDone || !loaded) return;
      gsap
        .timeline({
          delay: 0.5,
          onComplete: () => {
            setDone(true);
            ScrollTrigger.refresh();
          },
        })
        .add(() => onLoadComplete?.())
        .to(container.current, { ...FAR, duration: 1, ease: "power3.inOut" });
    },
    { scope: container, dependencies: [introDone, loaded] },
  );

  if (done) return null;

  return createPortal(
    <div className="preloader-container" ref={container}>
      <div className="preloader-content">
        <p className="preloader-title" aria-label={title}>
          {[...title].map((c, i) => (
            <span key={i} className="preloader-char" aria-hidden="true">
              {c === " " ? " " : c}
            </span>
          ))}
        </p>
        <p className="preloader-desc preloader-role">
          Design Engineer for <em className="accent">Human-Centered</em> AI Products
        </p>
        {desc && <p className="label preloader-desc">{desc}</p>}
      </div>
    </div>,
    document.body,
  );
}

Preloader.propTypes = {
  onLoadComplete: PropTypes.func,
  text1: PropTypes.string,
  text2: PropTypes.string,
};

export default Preloader;
